import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
const _excluded = ["background", "deserialize", "modifyText"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import app from '../common/app';
import ItemList from './utils/ItemList';
import Button from './components/Button';
import ModalManager from './components/ModalManager';
import AlertManager from './components/AlertManager';
import RequestErrorModal from './components/RequestErrorModal';
import Translator from './Translator';
import Store, { payloadIsPlural } from './Store';
import Session from './Session';
import extract from './utils/extract';
import extractText from './utils/extractText';
import Drawer from './utils/Drawer';
import mapRoutes from './utils/mapRoutes';
import RequestError from './utils/RequestError';
import ScrollListener from './utils/ScrollListener';
import liveHumanTimes from './utils/liveHumanTimes';
// @ts-expect-error We need to explicitly use the prefix to distinguish between the extend folder.
import { extend } from './extend.ts';
import Forum from './models/Forum';
import User from './models/User';
import Discussion from './models/Discussion';
import Post from './models/Post';
import Group from './models/Group';
import Notification from './models/Notification';
import PageState from './states/PageState';
import ModalManagerState from './states/ModalManagerState';
import AlertManagerState from './states/AlertManagerState';
import fireApplicationError from './helpers/fireApplicationError';
import AccessToken from './models/AccessToken';
import { ColorScheme } from './components/ThemeMode';
import { prepareSkipLinks } from './utils/a11y';

/**
 * A valid route definition.
 */

export let MaintenanceMode = /*#__PURE__*/function (MaintenanceMode) {
  MaintenanceMode["NO_MAINTENANCE"] = "none";
  MaintenanceMode["HIGH_MAINTENANCE"] = "high";
  MaintenanceMode["LOW_MAINTENANCE"] = "low";
  MaintenanceMode["SAFE_MODE"] = "safe";
  return MaintenanceMode;
}({});
/**
 * The `App` class provides a container for an application, as well as various
 * utilities for the rest of the app to use.
 */
export default class Application {
  constructor() {
    /**
     * The forum model for this application.
     */
    _defineProperty(this, "forum", void 0);
    /**
     * A map of routes, keyed by a unique route name. Each route is an object
     * containing the following properties:
     *
     * - `path` The path that the route is accessed at.
     * - `component` The Mithril component to render when this route is active.
     *
     * @example
     * app.routes.discussion = { path: '/d/:id', component: DiscussionPage };
     */
    _defineProperty(this, "routes", {});
    /**
     * An ordered list of initializers to bootstrap the application.
     */
    _defineProperty(this, "initializers", new ItemList());
    /**
     * The app's session.
     *
     * Stores info about the current user.
     */
    _defineProperty(this, "session", void 0);
    /**
     * The app's translator.
     */
    _defineProperty(this, "translator", new Translator());
    /**
     * The app's data store.
     */
    _defineProperty(this, "store", new Store({
      'access-tokens': AccessToken,
      forums: Forum,
      users: User,
      discussions: Discussion,
      posts: Post,
      groups: Group,
      notifications: Notification
    }));
    _defineProperty(this, "search", void 0);
    /**
     * A local cache that can be used to store data at the application level, so
     * that is persists between different routes.
     */
    _defineProperty(this, "cache", {});
    /**
     * Whether or not the app has been booted.
     */
    _defineProperty(this, "booted", false);
    /**
     * The page the app is currently on.
     *
     * This object holds information about the type of page we are currently
     * visiting, and sometimes additional arbitrary page state that may be
     * relevant to lower-level components.
     */
    _defineProperty(this, "current", new PageState(null));
    /**
     * The page the app was on before the current page.
     *
     * Once the application navigates to another page, the object previously
     * assigned to this.current will be moved to this.previous, while this.current
     * is re-initialized.
     */
    _defineProperty(this, "previous", new PageState(null));
    /**
     * An object that manages modal state.
     */
    _defineProperty(this, "modal", new ModalManagerState());
    /**
     * An object that manages the state of active alerts.
     */
    _defineProperty(this, "alerts", new AlertManagerState());
    /**
     * An object that manages the state of the navigation drawer.
     */
    _defineProperty(this, "drawer", void 0);
    _defineProperty(this, "history", null);
    _defineProperty(this, "pane", null);
    _defineProperty(this, "data", void 0);
    _defineProperty(this, "allowUserColorScheme", void 0);
    _defineProperty(this, "refs", {
      fontawesome: 'https://fontawesome.com/icons?o=r&m=free'
    });
    _defineProperty(this, "_title", '');
    _defineProperty(this, "_titleCount", 0);
    /**
     * The key for an Alert that was shown as a result of an AJAX request error.
     * If present, it will be dismissed on the next successful request.
     */
    _defineProperty(this, "requestErrorAlert", null);
    _defineProperty(this, "initialRoute", void 0);
    /**
     * @internal
     */
    _defineProperty(this, "currentInitializerExtension", null);
    _defineProperty(this, "handledErrors", []);
    _defineProperty(this, "beforeMounts", []);
  }
  set title(val) {
    this._title = val;
  }
  get title() {
    return this._title;
  }
  set titleCount(val) {
    this._titleCount = val;
  }
  get titleCount() {
    return this._titleCount;
  }
  load(payload) {
    this.data = payload;
    this.translator.setLocale(payload.locale);
  }
  initialize() {
    const caughtInitializationErrors = [];
    this.initializers.toArray().forEach(initializer => {
      this.currentInitializerExtension = initializer.itemName.includes('/') ? initializer.itemName.replace(/(\/flarum-ext-)|(\/flarum-)/g, '-') : initializer.itemName;
      try {
        initializer(this);
      } catch (e) {
        const extension = this.currentInitializerExtension;
        caughtInitializationErrors.push(() => fireApplicationError(extractText(app.translator.trans('core.lib.error.extension_initialiation_failed_message', {
          extension
        })), "".concat(extension, " failed to initialize"), e));
      }
    });
    return caughtInitializationErrors;
  }
  boot() {
    var _this$store$getById;
    const caughtInitializationErrors = this.initialize();
    this.store.pushPayload({
      data: this.data.resources
    });
    this.forum = this.store.getById('forums', '1');
    this.session = new Session((_this$store$getById = this.store.getById('users', String(this.data.session.userId))) != null ? _this$store$getById : null, this.data.session.csrfToken);
    this.runBeforeMount();
    this.mount();
    this.initialRoute = window.location.href;
    caughtInitializationErrors.forEach(handler => handler());
  }
  beforeMount(callback) {
    this.beforeMounts.push(callback);
  }
  runBeforeMount() {
    this.beforeMounts.forEach(callback => callback());
    this.beforeMounts = [];
  }
  bootExtensions(extensions) {
    Object.keys(extensions).forEach(name => {
      const extension = extensions[name];

      // If an extension doesn't define extenders, there's nothing more to do here.
      if (!extension.extend) return;
      const extenders = extension.extend.flat(Infinity);
      for (const extender of extenders) {
        extender.extend(this, {
          name,
          exports: extension
        });
      }
    });
  }
  mount(basePath) {
    if (basePath === void 0) {
      basePath = '';
    }
    // An object with a callable view property is used in order to pass arguments to the component; see https://mithril.js.org/mount.html
    m.mount(document.getElementById('modal'), {
      view: () => m(ModalManager, {
        state: this.modal
      })
    });
    m.mount(document.getElementById('alerts'), {
      view: () => m(AlertManager, {
        state: this.alerts
      })
    });
    this.drawer = new Drawer();
    m.route(document.getElementById('content'), basePath + '/', mapRoutes(this.routes, basePath));
    const appEl = document.getElementById('app');
    const appHeaderEl = document.querySelector('.App-header');

    // Add a class to the body which indicates that the page has been scrolled
    // down. When this happens, we'll add classes to the header and app body
    // which will set the navbar's position to fixed. We don't want to always
    // have it fixed, as that could overlap with custom headers.
    const scrollListener = new ScrollListener(top => {
      const offset = appEl.getBoundingClientRect().top + document.body.scrollTop;
      appEl.classList.toggle('affix', top >= offset);
      appEl.classList.toggle('scrolled', top > offset);
      appHeaderEl.classList.toggle('navbar-fixed-top', top >= offset);
    });
    scrollListener.start();
    scrollListener.update();
    document.body.classList.add('ontouchstart' in window ? 'touch' : 'no-touch');
    this.initColorScheme();
    liveHumanTimes();
    prepareSkipLinks();
  }
  initColorScheme(forumDefault) {
    var _app$forum$attribute, _this$session$user;
    if (forumDefault === void 0) {
      forumDefault = null;
    }
    forumDefault != null ? forumDefault : forumDefault = (_app$forum$attribute = app.forum.attribute('colorScheme')) != null ? _app$forum$attribute : 'auto';
    this.allowUserColorScheme = forumDefault === 'auto';
    const userConfiguredPreference = (_this$session$user = this.session.user) == null || (_this$session$user = _this$session$user.preferences()) == null ? void 0 : _this$session$user.colorScheme;
    let scheme;
    if (this.allowUserColorScheme) {
      scheme = userConfiguredPreference;
    }
    scheme || (scheme = forumDefault);
    this.setColorScheme(scheme);

    // Listen for browser color scheme changes and update the theme accordingly
    if (this.allowUserColorScheme) {
      this.watchSystemColorSchemePreference(() => {
        this.initColorScheme(forumDefault);
      });
    }
  }
  getSystemColorSchemePreference() {
    let colorScheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    if (window.matchMedia('(prefers-contrast: more)').matches) {
      colorScheme += '-hc';
    }
    return colorScheme;
  }
  watchSystemColorSchemePreference(callback) {
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', callback);
    window.matchMedia('(prefers-contrast: more)').addEventListener('change', callback);
  }
  setColorScheme(scheme) {
    if (scheme === ColorScheme.Auto) {
      scheme = this.getSystemColorSchemePreference();
    }
    document.documentElement.setAttribute('data-theme', scheme);
  }
  setColoredHeader(value) {
    document.documentElement.setAttribute('data-colored-header', value ? 'true' : 'false');
  }

  /**
   * Get the API response document that has been preloaded into the application.
   */

  preloadedApiDocument() {
    // If the URL has changed, the preloaded Api document is invalid.
    if (this.data.apiDocument && window.location.href === this.initialRoute) {
      const results = payloadIsPlural(this.data.apiDocument) ? this.store.pushPayload(this.data.apiDocument) : this.store.pushPayload(this.data.apiDocument);
      this.data.apiDocument = null;
      return results;
    }
    return null;
  }

  /**
   * Determine the current screen mode, based on our media queries.
   */
  screen() {
    const styles = getComputedStyle(document.documentElement);
    return styles.getPropertyValue('--flarum-screen');
  }

  /**
   * Set the `<title>` of the page.
   *
   * @param title New page title
   */
  setTitle(title) {
    this.title = title;
    this.updateTitle();
  }

  /**
   * Set a number to display in the `<title>` of the page.
   *
   * @param count Number to display in title
   */
  setTitleCount(count) {
    this.titleCount = count;
    this.updateTitle();
  }
  updateTitle() {
    const count = this.titleCount ? "(".concat(this.titleCount, ") ") : '';
    const onHomepage = m.route.get() === this.forum.attribute('basePath') + '/';
    const params = {
      pageTitle: this.title,
      forumName: this.forum.attribute('title'),
      // Until we add page numbers to the frontend, this is constant at 1
      // so that the page number portion doesn't show up in the URL.
      pageNumber: 1
    };
    let title = onHomepage || !this.title ? extractText(app.translator.trans('core.lib.meta_titles.without_page_title', params)) : extractText(app.translator.trans('core.lib.meta_titles.with_page_title', params));
    title = count + title;

    // We pass the title through a DOMParser to allow HTML entities
    // to be rendered correctly, while still preventing XSS attacks
    // from user input by using a script-disabled environment.
    // https://github.com/flarum/framework/issues/3514
    // https://github.com/flarum/framework/pull/3684
    // This is only a temporary solution for 1.x,
    // and the actual source of the issue will be fixed in 2.x
    // Actual source of the issue: https://github.com/flarum/framework/issues/3685
    const parser = new DOMParser();
    document.title = parser.parseFromString(title, 'text/html').body.innerText;
  }
  transformRequestOptions(flarumOptions) {
    const _flarumOptions = _objectSpread({}, flarumOptions),
      {
        background,
        deserialize,
        modifyText
      } = _flarumOptions,
      tmpOptions = _objectWithoutProperties(_flarumOptions, _excluded);

    // Unless specified otherwise, requests should run asynchronously in the
    // background, so that they don't prevent redraws from occurring.
    const defaultBackground = true;

    // When we deserialize JSON data, if for some reason the server has provided
    // a dud response, we don't want the application to crash. We'll show an
    // error message to the user instead.

    const defaultDeserialize = response => response;
    const options = _objectSpread({
      background: background != null ? background : defaultBackground,
      deserialize: deserialize != null ? deserialize : defaultDeserialize
    }, tmpOptions);
    extend(options, 'config', (_, xhr) => {
      xhr.setRequestHeader('X-CSRF-Token', this.session.csrfToken);
    });

    // If the method is something like PATCH or DELETE, which not all servers
    // and clients support, then we'll send it as a POST request with the
    // intended method specified in the X-HTTP-Method-Override header.
    if (options.method && !['GET', 'POST'].includes(options.method)) {
      const method = options.method;
      extend(options, 'config', (_, xhr) => {
        xhr.setRequestHeader('X-HTTP-Method-Override', method);
      });
      options.method = 'POST';
    }

    // When extracting the data from the response, we can check the server
    // response code and show an error message to the user if something's gone
    // awry.
    options.extract = xhr => {
      let responseText;
      if (modifyText) {
        responseText = modifyText(xhr.responseText);
      } else {
        responseText = xhr.responseText;
      }
      const status = xhr.status;
      if (status < 200 || status > 299) {
        throw new RequestError(status, "".concat(responseText), options, xhr);
      }
      if (xhr.getResponseHeader) {
        const csrfToken = xhr.getResponseHeader('X-CSRF-Token');
        if (csrfToken) app.session.csrfToken = csrfToken;
      }
      try {
        if (responseText === '') {
          return null;
        }
        return JSON.parse(responseText);
      } catch (e) {
        throw new RequestError(500, "".concat(responseText), options, xhr);
      }
    };
    return options;
  }

  /**
   * Make an AJAX request, handling any low-level errors that may occur.
   *
   * @see https://mithril.js.org/request.html
   */
  request(originalOptions) {
    const options = this.transformRequestOptions(originalOptions);
    if (this.requestErrorAlert) this.alerts.dismiss(this.requestErrorAlert);
    return m.request(options).catch(e => this.requestErrorCatch(e, originalOptions.errorHandler));
  }

  /**
   * By default, show an error alert, and log the error to the console.
   */
  async requestErrorCatch(error, customErrorHandler) {
    var _error$response$error, _error$response, _error$response2, _app$session$user;
    // the details property is decoded to transform escaped characters such as '\n'
    const formattedErrors = (_error$response$error = (_error$response = error.response) == null || (_error$response = _error$response.errors) == null ? void 0 : _error$response.map(e => {
      var _e$detail;
      return decodeURI((_e$detail = e.detail) != null ? _e$detail : '');
    })) != null ? _error$response$error : [];
    let content;
    switch (error.status) {
      case 422:
        content = formattedErrors.map(detail => [detail, m("br", null)]).flat().slice(0, -1);
        break;
      case 401:
      case 403:
        content = app.translator.trans('core.lib.error.permission_denied_message');
        break;
      case 404:
      case 410:
        content = app.translator.trans('core.lib.error.not_found_message');
        break;
      case 413:
        content = app.translator.trans('core.lib.error.payload_too_large_message');
        break;
      case 429:
        content = app.translator.trans('core.lib.error.rate_limit_exceeded_message');
        break;
      default:
        const code = (_error$response2 = error.response) == null || (_error$response2 = _error$response2.errors) == null || (_error$response2 = _error$response2[0]) == null ? void 0 : _error$response2.code;
        if (code === 'db_error' && (_app$session$user = app.session.user) != null && _app$session$user.isAdmin()) {
          content = app.translator.trans('core.lib.error.db_error_message');
        } else if (this.requestWasCrossOrigin(error)) {
          content = app.translator.trans('core.lib.error.generic_cross_origin_message');
        } else {
          content = app.translator.trans('core.lib.error.generic_message');
        }
    }
    const isDebug = app.forum.attribute('debug');
    error.alert = {
      type: 'error',
      content,
      controls: isDebug && [m(Button, {
        className: "Button Button--link",
        onclick: this.showDebug.bind(this, error, formattedErrors)
      }, app.translator.trans('core.lib.debug_button'))]
    };

    // Use the default error handler if no custom one was provided OR if we're handling falling back to the default.
    let useDefaultHandler = !customErrorHandler;
    if (customErrorHandler) {
      const output = await customErrorHandler(error);

      // If the custom handler returned false, we should fall back to the default handler.
      useDefaultHandler = output === false;
    }
    if (useDefaultHandler) {
      this.requestErrorDefaultHandler(error, isDebug, formattedErrors);
    }
    return Promise.reject(error);
  }

  /**
   * Used to modify the error message shown on the page to help troubleshooting.
   * While not certain, a failing cross-origin request likely indicates a missing redirect to Flarum canonical URL.
   * Because XHR errors do not expose CORS information, we can only compare the requested URL origin to the page origin.
   *
   * @param error
   * @protected
   */
  requestWasCrossOrigin(error) {
    var _error$options;
    return new URL((_error$options = error.options) == null ? void 0 : _error$options.url, document.baseURI).origin !== window.location.origin;
  }
  requestErrorDefaultHandler(e, isDebug, formattedErrors) {
    if (e instanceof RequestError) {
      if (isDebug && e.xhr) {
        const {
          method,
          url
        } = e.options;
        const {
          status = ''
        } = e.xhr;
        console.group("".concat(method, " ").concat(url, " ").concat(status));
        if (formattedErrors.length) {
          console.error(...formattedErrors);
        } else {
          console.error(e);
        }
        console.groupEnd();
      }
      if (e.status === 500 && isDebug) {
        app.modal.show(RequestErrorModal, {
          error: e,
          formattedError: formattedErrors
        });
      } else if (e.alert) {
        this.requestErrorAlert = this.alerts.show(e.alert, e.alert.content);
      }
    } else {
      throw e;
    }
  }
  showDebug(error, formattedError) {
    if (this.requestErrorAlert !== null) this.alerts.dismiss(this.requestErrorAlert);
    this.modal.show(RequestErrorModal, {
      error,
      formattedError
    });
  }

  /**
   * Construct a URL to the route with the given name.
   */
  route(name, params) {
    if (params === void 0) {
      params = {};
    }
    const route = this.routes[name];
    if (!route) throw new Error("Route '".concat(name, "' does not exist"));
    const url = route.path.replace(/:([^\/]+)/g, (m, key) => "".concat(extract(params, key)));

    // Remove falsy values in params to avoid having urls like '/?sort&q'
    for (const key in params) {
      if (params.hasOwnProperty(key) && !params[key]) delete params[key];
    }
    const queryString = m.buildQueryString(params);
    const prefix = m.route.prefix === '' ? this.forum.attribute('basePath') : '';
    return prefix + url + (queryString ? '?' + queryString : '');
  }
  handleErrorOnce(extension, errorId, userTitle, consoleTitle, error) {
    if (this.handledErrors.some(e => e.errorId === errorId)) return;
    this.handledErrors.push({
      extension,
      errorId,
      error
    });
    fireApplicationError(userTitle, consoleTitle, error);
  }
}
flarum.reg.add('core', 'common/Application', Application);